/**
 * PLAT-11227 (Faster TTFE, epic decision D4) — the publish gate's
 * enhancement leg: publishing must never silently ship raw audio as
 * enhanced. Unlock = enhancement-not-selected ∨ corrected renditions
 * present; terminal Auphonic failure opens the consent-downgrade escape
 * (explicit "publish with original audio?" confirm — raw publish is never
 * silent, never impossible).
 */

export type EnhancementGateState = 'open' | 'enhancing' | 'failed';

/** Structural subset of a story record the gate reads. */
export interface EnhancementGateStory {
  uploadLog?: {
    /**
     * Written by the versioned-keys pipeline (PLAT-11226) once corrected
     * audio-only + waveform exist. Legacy stories never carry this field.
     */
    audioCorrected?: { status?: string } | null;
    /** Written by the SFN failure-propagation writer on Auphonic timeout/terminal failure. */
    enhancementFailed?: { status?: string } | null;
  } | null;
  originalVideo?: {
    video?: {
      mp4UrlHd?: string | null;
      mp4UrlHigh?: string | null;
      mp4UrlMedium?: string | null;
      mp4UrlLow?: string | null;
    };
  } | null;
}

/**
 * The gate keys exclusively on signals only the NEW (versioned-keys)
 * pipeline writes — `audio_corrected` / `enhancement_failed`. Legacy
 * enhanced stories (Auphonic ran, renditions corrected IN PLACE, no
 * `-corrected` keys ever) must stay publishable: keying on
 * "submitToAuphonic ran ∧ no corrected renditions" would lock publishing
 * forever for every pre-epic enhanced story.
 *
 * Residual window, accepted for v1: on a new-pipeline story, raw renditions
 * can exist for a short stretch before Auphonic returns (`audio_corrected`
 * not yet written) — the gate reads open there. In practice corrected audio
 * lands well before the rendition set completes, so the window is thin; the
 * hard guarantee (never SILENTLY ship raw as enhanced once the pipeline
 * reports) holds from the moment either signal exists.
 */
export function getEnhancementGateState(
  story: EnhancementGateStory | null | undefined,
): EnhancementGateState {
  const log = story?.uploadLog;
  if (!log) return 'open';

  // populate prefers `-corrected` rendition keys once Replace Audio In
  // Transcodes has written them — corrected URLs mean the renditions the
  // publish would ship already carry enhanced audio.
  const video = story?.originalVideo?.video;
  const correctedRenditionsReady = [
    video?.mp4UrlHd,
    video?.mp4UrlHigh,
    video?.mp4UrlMedium,
    video?.mp4UrlLow,
  ].some(url => url?.includes('-corrected'));
  if (correctedRenditionsReady) return 'open';

  if (log.enhancementFailed?.status === 'fail') return 'failed';
  if (log.audioCorrected?.status === 'success') return 'enhancing';
  return 'open';
}
